import React, { useEffect, useRef, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { projectSchema } from "../utils/fileValidation";
import ProjectFileUpload from "./ProjectFileUpload";
import styles from "../styles.module.css";
import api from "api";
import AppModal from "components/ui/AppModal/AppModal";
import { clearProjectCache } from "utils/authUtils";

const PRODUCTS = [
  { value: "fotos",        label: "Fotos",         icon: "fa-camera" },
  { value: "filmagens",    label: "Filmagens",      icon: "fa-video" },
  { value: "gemeo_digital",label: "Gêmeo Digital",  icon: "fa-cube" },
  { value: "tour_360",     label: "Tour 360°",      icon: "fa-street-view" },
  { value: "geoview",  label: "GeoView",  icon: "fa-map" },
  { value: "bim",          label: "BIM",            icon: "fa-building" },
];

const ProjectForm = ({
  token,
  email,
  storageData,
  onDataChange,
  currentProjectCountInDB,
  maxProjectLimit,
  onSubmissionSuccess,
  existingProjectNames = [],
  setValueRef
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [modal, setModal] = useState({ open: false, variant: "custom", title: "", message: "", onConfirm: null });
  const modalResolveRef = useRef(null);

  /* Abre modal e retorna uma Promise que resolve quando fechado */
  const openModal = (config) =>
    new Promise(resolve => {
      modalResolveRef.current = resolve;
      setModal({ ...config, open: true });
    });

  const closeModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.();
    modalResolveRef.current = null;
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: { name: "", product: "", date: today, files: [] },
    resolver: yupResolver(projectSchema)
  });

  const selectedProduct = watch("product");
  const selectedFiles   = watch("files");
  const watchedName     = watch("name");
  const prevProductRef  = useRef(selectedProduct);

  // Expose setValue to parent via ref
  useLayoutEffect(() => {
    if (setValueRef) setValueRef.current = setValue;
  }, [setValueRef, setValue]);

  // Limpa arquivos ao trocar produto
  useEffect(() => {
    if (prevProductRef.current && prevProductRef.current !== selectedProduct && selectedFiles?.length > 0) {
      setValue("files", []);
      openModal({
        variant: "info",
        title: "Arquivos removidos",
        message: "Os arquivos foram removidos pois o tipo de projeto foi alterado.",
        onConfirm: closeModal,
      });
    }
    prevProductRef.current = selectedProduct;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  // Salva estado ao desmontar
  useEffect(() => {
    return () => { if (onDataChange) onDataChange(getValues()); };
  }, [getValues, onDataChange]);

  const isExistingProject = existingProjectNames.includes(watchedName?.trim());

  const onSubmit = async formData => {
    if (currentProjectCountInDB >= maxProjectLimit) {
      await openModal({
        variant: "error",
        title: "Limite atingido",
        message: "Você atingiu o limite de projetos do seu plano.",
        onConfirm: closeModal,
      });
      return;
    }

    const totalUploadSize = formData.files.reduce((s, f) => s + f.size, 0);
    const { maxStorageInBytes, usedStorageInBytes } = storageData;

    if (usedStorageInBytes + totalUploadSize > maxStorageInBytes) {
      await openModal({
        variant: "warning",
        title: "Espaço insuficiente",
        message: "Seu plano não suporta o tamanho deste upload. Considere fazer upgrade.",
        confirmText: "Ver Planos",
        onConfirm: () => { closeModal(); window.location.href = "/plans"; },
        cancelText: "Cancelar",
      });
      return;
    }

    const apiFormData = new FormData();
    apiFormData.append("name", formData.name);
    apiFormData.append("product", formData.product);
    apiFormData.append("date", formData.date);
    formData.files.forEach(file => apiFormData.append("files", file));

    try {
      setUploadProgress(0);

      // ⚠️ NUNCA defina Content-Type manualmente com FormData —
      // o navegador adiciona automaticamente o boundary correto.
      await api.post("/projects/create", apiFormData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: event => {
          if (!event.total) return;
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      });

      await openModal({
        variant: "success",
        title: "Projeto criado!",
        message: "Arquivos enviados com sucesso.",
        onConfirm: closeModal,
      });

      onSubmissionSuccess?.();

      // Invalida caches do dashboard e da página de projetos
      clearProjectCache(email);

      onDataChange?.(null);
      reset({ name: "", product: "", date: today, files: [] });
      setUploadProgress(null);
    } catch (error) {
      console.error("Erro ao criar projeto:", error);
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Ocorreu um erro ao enviar o projeto.";
      await openModal({ variant: "error", title: "Erro ao criar projeto", message: msg, onConfirm: closeModal });
      setUploadProgress(null);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.formLayout} noValidate>
      <div className={styles.formBody}>

        {/* Nome do projeto com autocomplete */}
        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.formLabel}>Nome do Projeto</label>
          <div className={styles.nameInputWrap}>
            <input
              id="name"
              type="text"
              list="project-names-list"
              {...register("name")}
              className={`${styles.formControl} ${errors.name ? styles.isInvalid : ""}`}
              placeholder="Digite ou selecione um projeto existente (vamos adicionar o produto na data escolhida)"
              disabled={isSubmitting}
              autoComplete="off"
            />
            {isExistingProject && (
              <span className={styles.existingBadge}>Projeto existente</span>
            )}
            <datalist id="project-names-list">
              {existingProjectNames.map(n => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
          {errors.name && <p className={styles.errorMessage}>{errors.name.message}</p>}
          {isExistingProject && (
            <p className={styles.formHint}>
              Este nome já existe na sua conta. Ao enviar, adicionaremos o produto selecionado
              para a data informada (você pode incluir mais produtos no mesmo projeto).
            </p>
          )}
        </div>

        {/* Produto — grade visual */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Tipo de Produto</label>
          <div className={styles.productGrid}>
            {PRODUCTS.map(p => (
              <button
                key={p.value}
                type="button"
                className={`${styles.productCard} ${selectedProduct === p.value ? styles["productCard--active"] : ""}`}
                onClick={() => setValue("product", p.value, { shouldValidate: true })}
                disabled={isSubmitting}
              >
                <i className={`fas ${p.icon}`} />
                {p.label}
              </button>
            ))}
          </div>
          {/* hidden input para validação */}
          <input type="hidden" {...register("product")} />
          {errors.product && <p className={styles.errorMessage}>{errors.product.message}</p>}
          {selectedProduct === "filmagens" && (
            <p className={styles.formHint}>Para melhor performance, envie vídeos com no máximo 1080p.</p>
          )}
        </div>

        {/* Data */}
        <div className={styles.formGroup}>
          <label htmlFor="date" className={styles.formLabel}>Data do Projeto</label>
          <input
            id="date"
            type="date"
            {...register("date")}
            className={`${styles.formControl} ${errors.date ? styles.isInvalid : ""}`}
            disabled={isSubmitting}
          />
          {errors.date && <p className={styles.errorMessage}>{errors.date.message}</p>}
        </div>

        {/* Arquivos com drag & drop */}
        <ProjectFileUpload
          control={control}
          name="files"
          product={selectedProduct}
          disabled={isSubmitting}
        />
      </div>

      {/* ── Modal unificado ───────────────────────────────────────── */}
      <AppModal
        isOpen={modal.open}
        onClose={closeModal}
        allowOutsideClick={true}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText || "OK"}
        onConfirm={modal.onConfirm}
        cancelText={modal.cancelText}
        onCancel={closeModal}
      />

      {/* Ações */}
      <div className={styles.formActions}>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonOutline}`}
          onClick={() => reset({ name: "", product: "", date: today, files: [] })}
          disabled={isSubmitting}
        >
          <i className="fas fa-undo" />
          Limpar
        </button>

        <button
          type="submit"
          className={`${styles.button} ${styles.buttonPrimary}`}
          disabled={isSubmitting || !selectedFiles || selectedFiles.length === 0}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Enviando...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane" />
              {isExistingProject ? "Adicionar produto ao Projeto" : "Criar Projeto"}
            </>
          )}
        </button>

        {uploadProgress !== null && (
          <div className={styles.uploadProgressWrap}>
            <div className={styles.uploadProgressLabel}>
              <span>Upload</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className={styles.uploadProgressBar}>
              <div className={styles.uploadProgressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default ProjectForm;
